import { createEntityId } from '@/core/EntityId';

/**
 * Reference photographs attached to a semantic tag.
 *
 * ## The gap this closes
 *
 * A tag says what a thing is in words, and words run out. "Split-rail cedar fence, silvered" is a
 * real improvement on an untagged grey box, and it still leaves the renderer to invent the rail
 * count, the post spacing, the joint, and whether the top rail is flat or round. For a fence, a
 * gate, a wall cap, a pergola — anything whose identity is *construction* rather than dimension —
 * the honest answer to "how should this look?" is a picture of one. So a tag may carry pictures, and
 * they travel with the tag into the render request.
 *
 * This is the same argument the prop system already made for meshes (`SiteObject.modelId`), arriving
 * one layer up: some things are identity, not parameters, and the right move is to name the artifact
 * rather than to invent a notation that approximates it badly.
 *
 * ## Why the bytes live in the document
 *
 * Inline as a data URL, downscaled hard, exactly like the forged ground swatches in
 * `SurfacePalette`. The alternative — a blob store keyed by id — keeps documents small and breaks
 * the one property this feature exists for: a `.landscape.json` handed to somebody else has to still
 * know how the fence looks. An image that survives only in the browser that made it would make the
 * exported file a worse description of the site than the one it was exported from.
 *
 * The cost is bounded rather than unbounded, which is what makes it payable — see
 * {@link TAG_IMAGE_BUDGET}. Every intake path downscales before the record is ever built, so a 12 MP
 * phone photograph enters the document at a few hundred kilobytes.
 *
 * ## Intent is not decoration
 *
 * An attached picture with no statement about *what is being pointed at* is "something like this",
 * which is the exact ambiguity tags exist to remove. Worse, it is dangerous in an image edit: a
 * model handed an unexplained photograph will cheerfully copy its background, its light, and its
 * camera angle into a frame that already has all three. So every image carries a
 * {@link TagImageIntent}, and the render prompt words each one differently — `match` asks for the
 * object to be reproduced, `mood` explicitly asks for nothing but the register.
 */

/**
 * What the attached picture is evidence *of*.
 *
 * Four levels, ordered from most to least binding, because these are the four things a designer
 * actually means when they attach a photograph to a thing on their site.
 */
export type TagImageIntent = 'match' | 'material' | 'mood' | 'avoid';

export const TAG_IMAGE_INTENTS: readonly TagImageIntent[] = ['match', 'material', 'mood', 'avoid'];

export interface TagImageIntentInfo {
  id: TagImageIntent;
  /** Title for the picker. */
  name: string;
  /** One line explaining what this asks the renderer to do. */
  hint: string;
}

export const TAG_IMAGE_INTENT_INFO: readonly TagImageIntentInfo[] = [
  {
    id: 'match',
    name: 'Build it like this',
    hint: 'Reproduce the thing itself — its construction, proportions and finish. For a fence, a gate, a wall cap.',
  },
  {
    id: 'material',
    name: 'This material',
    hint: 'Take the surface only: the stone, the timber, the weathering. Not the object it is part of.',
  },
  {
    id: 'mood',
    name: 'This feeling',
    hint: 'Palette, light and register only. Copies nothing.',
  },
  {
    id: 'avoid',
    name: 'Not like this',
    hint: 'A counter-example. Becomes a negative instruction.',
  },
];

export function tagImageIntentInfo(intent: TagImageIntent): TagImageIntentInfo {
  return (
    TAG_IMAGE_INTENT_INFO.find((info) => info.id === intent) ??
    (TAG_IMAGE_INTENT_INFO[0] as TagImageIntentInfo)
  );
}

export interface TagImage {
  id: string;
  /**
   * The picture itself, as a `data:` URL. Self-contained by design — see the note above about why
   * these bytes are in the document rather than beside it.
   */
  dataUrl: string;
  intent: TagImageIntent;
  widthPx: number;
  heightPx: number;
  /** Encoded size of `dataUrl`'s payload, so the UI can show what a tag costs without decoding it. */
  bytes: number;
  /** Original file name where there was one. Shown on the chip; never sent to a model. */
  name?: string;
  /**
   * What to look at in this particular picture — "the joint detail, not the colour". Optional, and
   * genuinely useful: one photograph often contains the answer to one question and noise around it.
   */
  caption?: string;
}

/**
 * The limits, in one place, stated as policy rather than scattered as magic numbers.
 *
 * `perTag` is three because a fourth picture of the same fence has never once been the thing that
 * made a render correct, and each one costs a numbered slot in a prompt legend the model has to hold
 * in its head. `perRender` is six for the same reason, plus a harder one: the enhance request already
 * spends three slots on the beauty, ID and depth passes, and a legend that runs to double figures
 * stops being read as a legend.
 */
export const TAG_IMAGE_BUDGET = {
  /** Images one tag may carry. */
  perTag: 3,
  /** Images that may reach one render request, across every tag on the site. */
  perRender: 6,
  /** Longest edge kept when a picture is taken in. */
  maxEdgePx: 1400,
  /** Ceiling on one stored image's encoded payload. Intake re-encodes until it fits. */
  maxBytes: 400_000,
  /** Ceiling on what every tag image on a site may cost the document, together. */
  maxSiteBytes: 4_000_000,
} as const;

export const MAX_TAG_IMAGE_CAPTION_LENGTH = 120;

export interface CreateTagImageOptions {
  dataUrl: string;
  intent?: TagImageIntent;
  widthPx?: number;
  heightPx?: number;
  name?: string;
  caption?: string;
  /** Overrides the generated id, so a test or an import can be deterministic. */
  id?: string;
}

export function createTagImage(options: CreateTagImageOptions): TagImage {
  const dataUrl = options.dataUrl.trim();
  const image: TagImage = {
    id: options.id ?? createEntityId('img-'),
    dataUrl,
    intent: TAG_IMAGE_INTENTS.includes(options.intent as TagImageIntent)
      ? (options.intent as TagImageIntent)
      : 'match',
    widthPx: positiveInt(options.widthPx, 0),
    heightPx: positiveInt(options.heightPx, 0),
    bytes: dataUrlBytes(dataUrl),
  };
  const name = trimmed(options.name);
  if (name !== undefined) image.name = name;
  const caption = normalizeCaption(options.caption);
  if (caption !== undefined) image.caption = caption;
  return image;
}

/** Collapse whitespace and clamp. Absent rather than empty — "no caption" is not "an empty caption". */
export function normalizeCaption(value: string | undefined): string | undefined {
  if (value === undefined) return undefined;
  const text = value.replace(/\s+/g, ' ').trim().slice(0, MAX_TAG_IMAGE_CAPTION_LENGTH);
  return text.length > 0 ? text : undefined;
}

/** Deep copy, so a stored or undo-held record never aliases a live one. */
export function cloneTagImage(image: TagImage): TagImage {
  const copy: TagImage = { ...image };
  if (image.name === undefined) delete copy.name;
  if (image.caption === undefined) delete copy.caption;
  return copy;
}

/**
 * Decoded byte count of a data URL's payload.
 *
 * Computed from the base64 length rather than by decoding: this is called on every render of a chip
 * and on every save, and decoding a megabyte to count it is work with no result. Base64 carries 3
 * bytes per 4 characters, minus the padding.
 */
export function dataUrlBytes(dataUrl: string): number {
  const comma = dataUrl.indexOf(',');
  if (comma < 0) return 0;
  const payload = dataUrl.slice(comma + 1);
  if (!dataUrl.slice(0, comma).includes('base64')) return payload.length;
  const padding = payload.endsWith('==') ? 2 : payload.endsWith('=') ? 1 : 0;
  return Math.max(0, Math.floor((payload.length * 3) / 4) - padding);
}

/** Whether a string is a data URL for an image this app is willing to store. */
export function isStorableImageDataUrl(value: unknown): value is string {
  return typeof value === 'string' && /^data:image\/(png|jpeg|webp);base64,[A-Za-z0-9+/=]+$/.test(value);
}

/**
 * Take a list of images down to what one tag may hold.
 *
 * Order is preserved — the designer attached them in the order they thought of them, and reordering
 * a legend to suit a budget is a change they did not ask for. Anything unstorable is dropped rather
 * than repaired: a broken data URL in a prompt is a wasted image slot and a confusing legend.
 */
export function fitTagImages(images: readonly TagImage[]): TagImage[] {
  const kept: TagImage[] = [];
  for (const image of images) {
    if (kept.length >= TAG_IMAGE_BUDGET.perTag) break;
    if (!isStorableImageDataUrl(image.dataUrl)) continue;
    kept.push(cloneTagImage(image));
  }
  return kept;
}

/** Total encoded cost of a set of images, for the "what does this site carry?" readout. */
export function totalImageBytes(images: readonly TagImage[]): number {
  return images.reduce((total, image) => total + Math.max(0, image.bytes), 0);
}

/** Human size for a chip or a budget line — `240 KB`, `1.8 MB`. */
export function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${Math.max(0, Math.round(bytes))} B`;
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

/* ------------------------------------- Parsing ------------------------------------- */

/**
 * Read one image from a stored document.
 *
 * Returns undefined rather than throwing, on the same principle the tag reader already follows: a
 * project whose annotations are damaged is still a project, and refusing to open it would trade a
 * lost picture for a lost design.
 */
export function parseTagImage(raw: unknown): TagImage | undefined {
  if (typeof raw !== 'object' || raw === null || Array.isArray(raw)) return undefined;
  const data = raw as Record<string, unknown>;

  const id = trimmed(data.id);
  if (!id) return undefined;
  if (!isStorableImageDataUrl(data.dataUrl)) return undefined;

  return createTagImage({
    id,
    dataUrl: data.dataUrl,
    ...(TAG_IMAGE_INTENTS.includes(data.intent as TagImageIntent)
      ? { intent: data.intent as TagImageIntent }
      : {}),
    ...(typeof data.widthPx === 'number' ? { widthPx: data.widthPx } : {}),
    ...(typeof data.heightPx === 'number' ? { heightPx: data.heightPx } : {}),
    ...(trimmed(data.name) !== undefined ? { name: trimmed(data.name) as string } : {}),
    ...(trimmed(data.caption) !== undefined ? { caption: trimmed(data.caption) as string } : {}),
  });
}

/** Read a whole list, dropping what cannot be read and holding to the per-tag budget. */
export function parseTagImages(raw: unknown): TagImage[] {
  if (!Array.isArray(raw)) return [];
  const images: TagImage[] = [];
  for (const entry of raw) {
    const image = parseTagImage(entry);
    if (image) images.push(image);
  }
  return fitTagImages(images);
}

/* ------------------------------------- Helpers ------------------------------------- */

function trimmed(value: unknown): string | undefined {
  return typeof value === 'string' && value.trim().length > 0 ? value.trim() : undefined;
}

function positiveInt(value: number | undefined, fallback: number): number {
  if (value === undefined || !Number.isFinite(value) || value <= 0) return fallback;
  return Math.round(value);
}
